import { formatOdds, formatPoint, formatTotalPoint } from "./odds";
import {
  calcRiskFromWin,
  calcWinFromRisk,
  roundMoney,
} from "../components/lib/wagerFinancials";
import { getFeaturedMarketLabel } from "./complianceCopy";
import { isDTS } from "./platform";

export const callBetMarketLabel = (oddType) =>
  getFeaturedMarketLabel(oddType, isDTS());

const finiteNumber = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Saved call orders default bookPoint/bookPrice to 0 when only original* was written.
 * A real pick'em is 0 on both. American odds are never 0, so 0 there means unset.
 */
const preferStoredBookValue = (bookValue, originalValue) => {
  const book = finiteNumber(bookValue);
  const original = finiteNumber(originalValue);
  if (book != null && book !== 0) return book;
  if (original != null && original !== 0) return original;
  return book ?? original;
};

export const getCallBetBookPoint = (item) => {
  const stored = preferStoredBookValue(item?.bookPoint, item?.originalPoint);
  if (stored != null) return stored;
  if (!item?.isCallBetMode && item?.betstatus !== "NotActivated") {
    return finiteNumber(item?.point);
  }
  return null;
};

export const getCallBetBookPrice = (item) => {
  const stored = preferStoredBookValue(item?.bookPrice, item?.originalPrice);
  if (stored != null && stored !== 0) return stored;
  const price = finiteNumber(item?.price);
  if (price != null && price !== 0) return price;
  return stored ?? price;
};

export const getCallBetDesiredPoint = (item) => {
  const raw =
    item?.customizedPoint ??
    item?.desiredPoint ??
    (item?.betstatus === "NotActivated" || item?.isCallBetMode
      ? item?.point
      : null);
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

export const getCallBetDesiredPrice = (item) => {
  const raw =
    item?.desiredPrice ??
    (item?.oddType === "h2h" || item?.type === "h2h"
      ? item?.price
      : null);
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/** Book line at placement — spread/total show line @ price; ML shows price only. */
export const formatCallBetBookLine = (item) => {
  if (!item) return "-";
  if (item.type === "h2h" || item.oddType === "h2h") {
    const price = getCallBetBookPrice(item);
    return price == null ? "-" : formatOdds(price);
  }
  const point = getCallBetBookPoint(item);
  const price = getCallBetBookPrice(item);
  if (point == null) return "-";
  const line =
    item.type === "totals" || item.oddType === "totals"
      ? formatTotalPoint(point, item.pointType || item.teamName)
      : formatPoint(point);
  return price == null ? line : `${line} @ ${formatOdds(price)}`;
};

/** User's limit-order target. */
export const formatCallBetDesiredLine = (item) => {
  if (!item) return "-";
  if (item.type === "h2h" || item.oddType === "h2h") {
    const price = getCallBetDesiredPrice(item);
    return price == null ? "-" : formatOdds(price);
  }
  const point = getCallBetDesiredPoint(item);
  if (point == null) return "-";
  if (item.type === "totals" || item.oddType === "totals") {
    return formatTotalPoint(point, item.pointType || item.teamName);
  }
  return formatPoint(point);
};

export const formatCallBetComparison = (item) => {
  const book = formatCallBetBookLine(item);
  const desired = formatCallBetDesiredLine(item);
  if (book === "-" || desired === "-" || book === desired) return null;
  return { book, desired };
};

/**
 * Odds used for call-bet risk/win/payout preview and submit.
 * M-Line: desired price when entered; spreads/totals: book juice (line is separate).
 */
export const resolveCallBetPriceForPayout = (item) => {
  if (!item) return null;

  const isH2h = item.type === "h2h" || item.oddType === "h2h";
  if (isH2h) {
    const raw = item.desiredPrice;
    if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
      const desired = Number(raw);
      if (Number.isFinite(desired)) return desired;
    }
  }

  const book = getCallBetBookPrice(item);
  if (book != null) return book;

  const fallback = Number(item.price);
  return Number.isFinite(fallback) ? fallback : null;
};

/** Recompute win/payout when desired M-Line price changes but risk/win is already set. */
export const recalculateCallBetPayout = (item) => {
  if (!item) return item;

  const price = resolveCallBetPriceForPayout(item);
  if (!Number.isFinite(price)) return item;

  const risk = Number(item.risk);
  const win = Number(item.win);

  if (risk > 0) {
    const nextWin = calcWinFromRisk(risk, price);
    return {
      ...item,
      win: nextWin,
      payout: roundMoney(risk + nextWin),
      Price: item.Price ?? risk,
    };
  }

  if (win > 0) {
    const nextRisk = calcRiskFromWin(win, price);
    return {
      ...item,
      risk: nextRisk,
      Price: nextRisk,
      payout: roundMoney(nextRisk + win),
    };
  }

  return item;
}; 
